import { useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Avatar, Box, Button, darken, Divider, lighten, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getData, selectRowHeader } from '@/redux/slices/dataTableSlice';
import {
  selectCheckedTableRows,
  selectCurrentTableBulkActions,
  selectCheckedTableRowsStatusStageCounts,
  selectCurrentTableKey
} from '@/redux/selectors/dataTableSelectors';
import { displayBulkActionIdent } from '@/_utils/utils';
import { IconDictionary } from '@/components/_common/PolarisIcon';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import BulkActionDialog from '@/components/_common/BulkActionDialog';

const BulkActionBar = () => {
  const dispatch = useAppDispatch();
  const tableKey = useAppSelector(selectCurrentTableKey);
  const selectActions = useAppSelector(selectCurrentTableBulkActions);
  const selectedRows = useAppSelector(selectCheckedTableRows);
  const selectStatusStageCounts = useAppSelector(selectCheckedTableRowsStatusStageCounts);
  const bulkActionIcons = useContext(ConfigContext)?.bulkActionIcons;
  const [isBulkActionDialogOpen, setIsBulkActionDialogOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<string | undefined>(undefined);

  const actionsArray = useMemo(() => {
    if (!selectActions) return [];
    return Object.keys(selectActions).map((key) => {
      const action = selectActions[key];
      let count = 0;
      action.allowed_statuses.forEach((status) => {
        if (status.stage)
          return (count += selectStatusStageCounts[status.status] ? selectStatusStageCounts[status.status].stages[status.stage] || 0 : 0);
        if (!selectStatusStageCounts[status.status]) return;
        count += selectStatusStageCounts[status.status].count || 0;
        if (selectStatusStageCounts[status.status].stages && Object.keys(selectStatusStageCounts[status.status].stages).length) {
          count += Object.values(selectStatusStageCounts[status.status].stages as number).reduce((acc, stage) => acc + stage, 0);
        }
      });
      return { ...action, count, key };
    });
  }, [selectActions, selectStatusStageCounts]);

  const handleBulkActionConfirm = async (maintainSelections: boolean) => {
    setIsBulkActionDialogOpen(false);
    try {
      if (!maintainSelections) {
        dispatch(selectRowHeader([]));
        dispatch(getData());
        return;
      }

      dispatch(getData());
    } catch (error) {
      console.error('Failed to load table data', error);
    }
  };

  return (
    <>
      <Wrapper>
        <Box>
          <StyledTotalRecordsCount>{selectedRows.length}</StyledTotalRecordsCount> Selected {displayBulkActionIdent(tableKey || 'items')}
        </Box>
        <Divider orientation="vertical" flexItem />
        {actionsArray.map((action) => (
          <StyledButton
            key={action.key}
            variant="text"
            startIcon={
              <PolarisIcon
                icon={
                  action.icon ||
                  bulkActionIcons?.[action.key] ||
                  (IconDictionary[action.key] && typeof IconDictionary[action.key] === 'string' ? IconDictionary[action.key] : 'edit')
                }
              />
            }
            disabled={!action.count}
            count={action.count}
            onClick={() => {
              setActiveAction(action.key);
              setIsBulkActionDialogOpen(true);
            }}
          >
            <Typography>{displayBulkActionIdent(action.ident)}</Typography>
            <StyledAvatar count={action.count}>{action.count}</StyledAvatar>
          </StyledButton>
        ))}
      </Wrapper>
      {isBulkActionDialogOpen && (
        <BulkActionDialog
          action={activeAction!}
          isOpen={isBulkActionDialogOpen}
          onConfirm={handleBulkActionConfirm}
          onCancel={() => setIsBulkActionDialogOpen(false)}
        />
      )}
    </>
  );
};

const Wrapper = styled.div`
  position: fixed;
  z-index: 1;
  bottom: 13.5rem;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  column-gap: 1rem;
  min-width: 250px;
  width: fit-content;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  background: ${({ theme }) =>
    theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.06) : lighten(theme.palette.background.paper, 0.06)};
  border: ${({ theme }) => `2px solid ${theme.palette.background.paper}`};
  border-radius: 2.5em;
  box-shadow: ${({ theme }) => `0px 15px 50px ${theme.palette.background.paper}`};
  & > .MuiTypography-root {
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const StyledTotalRecordsCount = styled.div`
  display: inline-block;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 1.25em;
  margin-right: 0.25em;
`;

const StyledButton = styled(Button)<{ count: number }>`
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.background.default)};
  border-radius: 2.5rem;
  & > .MuiButton-icon {
    color: ${({ theme, count }) => (count > 0 ? theme.palette.primary.main : theme.palette.grey[400])};
  }
`;

const StyledAvatar = styled(Avatar)<{ count: number }>`
  min-width: 25px;
  min-height: 25px;
  max-width: 32px;
  max-height: 32px;
  margin-left: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme, count }) => (count > 0 ? theme.palette.primary.main : theme.palette.grey[400])};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  font-size: 1rem;
  font-weight: normal;
`;

export default BulkActionBar;
